/**
 * « Mes sinistres » (`extranet.claimsListCard`, fiches 22736 / 22989): the
 * claims of the session in an integrated `DataList` — inline rows in API
 * order, clickable rows opening `/sinistres/{ID_SIN}` with the row's
 * insured / product / reference in the router state. Below 768px `DataList`
 * renders its own mobile card layout and the toolbar stacks.
 *
 * Toolbar (fiche 22989) in a card detached above the table: the search
 * « Rechercher par réf, circonstances, assuré… » and the status select
 * (stable filter id `statut`: « Tous statuts » + the
 * `WS2_EA_SEL_STATUT_SINISTRE` options, fetched here). Both are
 * **server-side**: the toolbar state goes up through `onFiltersChange`
 * (search debounced 300 ms → `q`, select → `etat`, blank ⇒ not sent) and the
 * page re-fetches `WS2_EA_TAB_SINISTRES`; nothing is filtered client-side.
 *
 * Configurable-ready: columns, filter and search come from the declarative
 * config of `./claimsListConfig.ts` (stable keys); the shared
 * `DataTableCard` host (detached toolbar, server-filtered inline mode)
 * merges `_layoutOverrides` (columns rename / hide / show / reorder /
 * tooltips, default sort, filter and search overrides, title) and publishes
 * the base config under `componentId`. No base title: the page header
 * carries « Mes sinistres » and the count. An empty result shows
 * « aucune donnée disponible » under the toolbar.
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

import type { LayoutOverrides } from '@components/shared/configurable';

import {
  CLAIM_ENDPOINTS,
  fetchClaimStatusOptions,
  type ClaimListRow,
  type ClaimsListFilters,
} from '../../api/claimsService';
import { useSelOptions } from '../../hooks/useSelOptions';
import { extranetPath } from '../../navigation';
import type { ExtranetRowContext } from '../shared/cellRenderers';
import { DataTableCard } from '../shared/DataTableCard';
import type { ListToolbarQuery } from '../shared/listToolbarConfig';
import { claimRouteStateOf } from './claimRouteState';
import {
  CLAIMS_STATUS_FILTER_ID,
  buildClaimsListConfig,
  type ClaimsListDataRow,
} from './claimsListConfig';

export interface ClaimsListCardProps {
  /** Registered id of this usage (bound by `bindCardProps`). */
  componentId: string;
  /** Base title (overridable); omitted ⇒ no title row (the page header titles the screen). */
  title?: string;
  icon?: LucideIcon;
  rows: readonly ClaimListRow[];
  loading?: boolean;
  error?: string | null;
  /** Toolbar state as `WS2_EA_TAB_SINISTRES` filters (on mount and on change) — page-wired. */
  onFiltersChange?: (filters: ClaimsListFilters) => void;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

export function ClaimsListCard({
  componentId,
  title,
  icon,
  rows,
  loading = false,
  error = null,
  onFiltersChange,
  _layoutOverrides,
}: Readonly<ClaimsListCardProps>) {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const statusOptions = useSelOptions(CLAIM_ENDPOINTS.statusOptions, fetchClaimStatusOptions);

  // Per-row context: the detail path + router state of each row, rebuilt
  // with the rows so the singleton row-click handler reads fresh closures.
  const listRows = useMemo<ClaimsListDataRow[]>(
    () =>
      rows.map((row) => {
        const idSin = row.idSin;
        const ctx: ExtranetRowContext = {
          onOpen:
            idSin === null
              ? undefined
              : () => {
                  void navigate(extranetPath(tenantSlug, 'sinistres', idSin), {
                    state: claimRouteStateOf(row),
                  });
                },
        };
        return { ...row, __ctx: ctx };
      }),
    [rows, navigate, tenantSlug],
  );

  const baseConfig = useMemo(
    () => buildClaimsListConfig(componentId, listRows, statusOptions),
    [componentId, listRows, statusOptions],
  );

  // Toolbar → VEOS filters: search → `q`, status select → `etat`.
  const handleToolbarChange = useCallback(
    (query: ListToolbarQuery) => {
      onFiltersChange?.({
        q: query.search,
        etat: query.filters[CLAIMS_STATUS_FILTER_ID] ?? '',
      });
    },
    [onFiltersChange],
  );

  return (
    <DataTableCard
      componentId={componentId}
      title={title}
      icon={icon}
      config={baseConfig}
      rowCount={rows.length}
      loading={loading}
      error={error}
      toolbar
      toolbarPlacement="detached"
      onToolbarChange={handleToolbarChange}
      _layoutOverrides={_layoutOverrides}
    />
  );
}
